/**
 * Board / BoardColumn / BoardCard / BoardEmpty — Board Pattern del
 * DESIGN_SYSTEM.
 *
 * Un tablero es una lista partida en columnas por etapa, donde la posición
 * dentro de la columna la decide el usuario arrastrando. Sin props de
 * `className` ni `style`: las variantes son union types y todo valor sale de
 * los tokens --evo-board-* (REGLA #16).
 */
import type { ReactNode } from 'react';
import type { FunnelStageTone } from '@evo-hub/shared';
import { Icon } from '../Icon.js';
import { BARRA_PENDIENTE } from './ListItem.js';

/**
 * Tono de etapa → token de color. ÚNICO lugar del front donde una clave neutra
 * se convierte en color: el componente nunca recibe un hex (Board Pattern,
 * "el color lo pone la etapa, no el diseñador").
 */
export const STAGE_TONE: Record<FunnelStageTone, string> = {
  neutral: 'var(--evo-stage-new)',
  info: 'var(--evo-stage-analyzed)',
  action: 'var(--evo-stage-drafted)',
  progress: 'var(--evo-stage-sent)',
  won: 'var(--evo-stage-won)',
  lost: 'var(--evo-stage-lost)',
};

/** Contador de columna con cero a la izquierda: 05, 00, 124. */
export function padCount(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ── Board ─────────────────────────────────────────────────────────────────────

export function Board({ children, tourId }: { children: ReactNode; tourId?: string }) {
  return (
    <div
      data-tour-id={tourId}
      style={{
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: 'var(--evo-space-5) var(--evo-page-pad-x)',
        display: 'flex',
        // Todas las columnas miden lo mismo tengan una carta o doce.
        alignItems: 'stretch',
        gap: 'var(--evo-board-gap)',
      }}
    >
      {children}
    </div>
  );
}

// ── BoardColumn ───────────────────────────────────────────────────────────────

export interface BoardColumnProps {
  name: string;
  count: number;
  /** Suma de la columna, ya formateada por el caller. */
  total?: string;
  tone: FunnelStageTone;
  /** Etiqueta del chip de ingesta automática. */
  intakeLabel?: string;
  /** Rótulo de resultado (GANADA / PERDIDA), ya traducido. */
  outcomeLabel?: string;
  /** Resalta la columna mientras se arrastra una carta encima. */
  isDropTarget?: boolean;
  children: ReactNode;
}

export function BoardColumn({
  name, count, total, tone, intakeLabel, outcomeLabel, isDropTarget = false, children,
}: BoardColumnProps) {
  return (
    <div
      style={{
        width: 'var(--evo-board-col-w)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${isDropTarget ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
        borderRadius: 'var(--evo-r-md)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        transition: 'border-color 120ms linear',
      }}
    >
      {/* Barra de tono: la ÚNICA expresión del color de la etapa. */}
      <div style={{ height: 'var(--evo-board-tone-bw)', flexShrink: 0, background: STAGE_TONE[tone] }} />

      <div
        style={{
          flexShrink: 0,
          minHeight: 'var(--evo-board-header-h)',
          boxSizing: 'border-box',
          padding: 'var(--evo-card-head-pad)',
          background: 'var(--evo-bg-2)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-card-head-gap)' }}>
          <span
            style={{
              flex: 1, minWidth: 0,
              fontSize: 'var(--evo-fs-caption)',
              fontWeight: 'var(--evo-list-header-fw)',
              letterSpacing: 'var(--evo-ls-caps)',
              textTransform: 'uppercase',
              color: 'var(--evo-fg-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          {/* Texto, no badge cian: seis badges cian en un tablero dejan de señalar nada. */}
          <span
            style={{
              flexShrink: 0,
              fontSize: 'var(--evo-fs-caption)',
              color: 'var(--evo-fg-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {padCount(count)}
          </span>
        </div>

        {/* Esta fila reserva su línea aunque esté vacía: sin eso las columnas
            dejan de medir igual y las cartas no arrancan en la misma y. */}
        <div
          style={{
            display: 'flex', alignItems: 'center',
            gap: 'var(--evo-space-2)',
            minHeight: 'var(--evo-pill-h-xs)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {total != null && (
              <span
                style={{
                  fontSize: 'var(--evo-fs-body)',
                  color: count === 0 ? 'var(--evo-fg-4)' : 'var(--evo-fg-1)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {total}
              </span>
            )}
          </div>
          {intakeLabel != null && (
            <span
              style={{
                flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                height: 'var(--evo-pill-h-xs)',
                padding: '0 var(--evo-pill-px)',
                borderRadius: 'var(--evo-r-pill)',
                background: 'var(--evo-cyan-12)',
                color: 'var(--evo-cyan)',
                fontSize: 'var(--evo-fs-tiny)',
                letterSpacing: 'var(--evo-ls-caps)',
                textTransform: 'uppercase',
                lineHeight: 1,
                boxSizing: 'border-box',
              }}
            >
              <Icon name="bolt" size={10} />
              {intakeLabel}
            </span>
          )}
          {intakeLabel == null && outcomeLabel != null && (
            <span
              style={{
                flexShrink: 0,
                fontSize: 'var(--evo-fs-tiny)',
                color: 'var(--evo-fg-4)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--evo-ls-caps)',
              }}
            >
              {outcomeLabel}
            </span>
          )}
        </div>
      </div>

      <div
        style={{
          flex: 1, minHeight: 0,
          overflowY: 'auto',
          padding: 'var(--evo-board-gap)',
          display: 'flex', flexDirection: 'column',
          gap: 'var(--evo-board-card-gap)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── BoardCard ─────────────────────────────────────────────────────────────────

export interface BoardCardProps {
  title: string;
  /** De dónde viene: formulario, cotización, proyecto. */
  subtitle?: string;
  /** Valor ya formateado por el caller. */
  value?: string;
  /** Antigüedad en la etapa, ya formateada ("hace 2h"). */
  age?: string;
  /** `stale` = más de 7 días en la etapa; se pinta en ámbar. */
  ageTone?: 'normal' | 'stale';
  pills?: ReactNode;
  /**
   * Acciones de la carta — como mucho dos, en iconos, abajo a la derecha.
   * Nacen del caso de Embudos: volver la respuesta de un formulario en
   * contacto, y volverla cotización. Van acá y no en el panel porque son el
   * trabajo que se hace mirando la columna, sin abrir nada.
   */
  actions?: ReactNode;
  /**
   * Llegó desde la última vez que se miró el módulo. Barra cian a la
   * izquierda, la misma de `ListItem` — apagar el contador del menú sin
   * señalar CUÁLES eran deja el número en cero y nada que mirar.
   */
  nueva?: boolean;
  selected?: boolean;
  /** Baja la opacidad mientras la carta se está arrastrando. */
  dragging?: boolean;
  /**
   * Es la copia que viaja con el cursor, no la de la columna.
   *
   * Levanta con `--evo-menu-shadow` y borde cian. La regla del pattern —"nunca
   * sombra"— habla de la carta EN REPOSO dentro de su columna; esta está
   * literalmente flotando sobre el tablero, y sin elevación se lee como si
   * estuviera pegada abajo en vez de en la mano.
   */
  arrastrando?: boolean;
  onOpen: () => void;
}

export function BoardCard({
  title, subtitle, value, age, ageTone = 'normal', pills, actions,
  nueva = false, selected = false, dragging = false, arrastrando = false, onOpen,
}: BoardCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      style={{
        background: 'var(--evo-panel)',
        border: `1px solid ${selected || arrastrando ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
        borderRadius: 'var(--evo-r-md)',
        padding: 'var(--evo-board-card-pad)',
        display: 'flex', flexDirection: 'column', gap: '4px',
        boxShadow: arrastrando
          ? 'var(--evo-menu-shadow)'
          : nueva ? BARRA_PENDIENTE : 'none',
        cursor: arrastrando ? 'grabbing' : 'grab',
        // La que queda en la columna se atenúa: marca de dónde salió, sin
        // dejar el hueco vacío que hacía perder la referencia.
        opacity: dragging ? 0.4 : 1,
        boxSizing: 'border-box',
        transition: 'border-color 120ms linear, opacity 120ms linear',
      }}
    >
      <span
        style={{
          fontSize: 'var(--evo-list-title-fs)',
          color: 'var(--evo-fg-1)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
      {subtitle != null && (
        <span
          style={{
            fontSize: 'var(--evo-fs-caption)',
            color: 'var(--evo-fg-3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </span>
      )}
      {(value != null || age != null) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', marginTop: '2px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {value != null && (
              <span
                style={{
                  fontSize: 'var(--evo-list-title-fs)',
                  fontWeight: 'var(--evo-fw-medium)',
                  color: 'var(--evo-fg-1)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {value}
              </span>
            )}
          </div>
          {age != null && (
            <span
              style={{
                flexShrink: 0,
                fontSize: 'var(--evo-fs-tiny)',
                color: ageTone === 'stale' ? 'var(--evo-amber)' : 'var(--evo-fg-4)',
                whiteSpace: 'nowrap',
              }}
            >
              {age}
            </span>
          )}
        </div>
      )}
      {(pills != null || actions != null) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {pills}
          </div>
          {actions != null && (
            // El clic de una acción no puede además abrir el panel: son dos
            // cosas distintas y el panel taparía lo que la acción acaba de hacer.
            <div
              style={{ flexShrink: 0, display: 'flex', gap: '4px' }}
              onClick={e => e.stopPropagation()}
              onPointerDown={e => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── BoardEmpty ────────────────────────────────────────────────────────────────

/** Columna vacía = zona de suelta. Nunca se deja en blanco. */
export function BoardEmpty({ label }: { label: string }) {
  return (
    <div
      style={{
        minHeight: 'var(--evo-board-drop-min-h)',
        border: '1px dashed var(--evo-stroke)',
        borderRadius: 'var(--evo-r-md)',
        background: 'var(--evo-hatch)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--evo-board-card-pad)',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          fontSize: 'var(--evo-fs-caption)',
          color: 'var(--evo-fg-4)',
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        {label}
      </span>
    </div>
  );
}
